import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const emptyForm = { receiver_company_id: '', item_barcode: '', limit: '' };
const fields = ['sender_company_id', 'receiver_company_id', 'item_barcode', 'limit'];

function LimitFields({ prefix, companyId, value, onChange, disabled }) {
  const { t } = useTranslation();
  return <div className="items-form-grid">
    {fields.map(field => <div className="field" key={field}>
      <label htmlFor={`${prefix}-${field}`}><strong>{t(`item_limits.${field}`)}</strong></label>
      <input
        id={`${prefix}-${field}`} className="input" type="text" required
        value={field === 'sender_company_id' ? companyId : value[field]}
        readOnly={field === 'sender_company_id'} disabled={disabled}
        maxLength={field === 'item_barcode' ? 255 : 20}
        inputMode={field === 'limit' ? 'decimal' : undefined}
        pattern={field === 'limit' ? '[0-9]{1,12}([.][0-9]{1,6})?' : undefined}
        title={field === 'limit' ? t('item_limits.limit_help') : undefined}
        onChange={event => onChange(previous => ({ ...previous, [field]: event.target.value }))}
      />
    </div>)}
  </div>;
}

export default function ItemLimits({ companyId, authHeaders, apiFetch }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [edit, setEdit] = useState(null);
  const [filterDraft, setFilterDraft] = useState({ receiver_company_id: '', item_barcode: '' });
  const [filters, setFilters] = useState(filterDraft);
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);

  const request = useCallback(async (path = '', options = {}) => {
    const token = localStorage.getItem('token');
    if (!token) throw new Error(t('no_token_error'));
    const response = await apiFetch(`/api/item-limits${path}`, {
      ...options, headers: authHeaders(token, options.headers),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || t('item_limits.request_failed'));
    }
    return response;
  }, [apiFetch, authHeaders, t]);

  const load = useCallback(async signal => {
    setLoading(true);
    try {
      const response = await request(`?${new URLSearchParams(filters)}`, { signal });
      const data = await response.json();
      if (!signal?.aborted) setRows(data.item_limits || []);
    } catch (err) {
      if (!signal?.aborted) setError(err.message);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [filters, request]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function perform(action) {
    setBusy(true);
    setError('');
    setFeedback('');
    try { await action(); } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  function save(event, editing = false) {
    event.preventDefault();
    const value = editing ? edit : form;
    return perform(async () => {
      await request(editing ? `/${value.id}` : '', {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...value, sender_company_id: companyId }),
      });
      if (editing) setEdit(null); else setForm(emptyForm);
      setFeedback(t('item_limits.saved'));
      await load();
    });
  }

  function remove(row) {
    if (!window.confirm(t('item_limits.delete_confirm', { barcode: row.item_barcode, receiver: row.receiver_company_id }))) return;
    return perform(async () => {
      await request(`/${row.id}`, { method: 'DELETE' });
      setFeedback(t('item_limits.deleted'));
      await load();
    });
  }

  function download(kind) {
    return perform(async () => {
      const response = await request(`/${kind}`);
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = kind === 'template' ? 'item_limits_template.xlsx' : 'item_limits.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      setFeedback(t('item_limits.downloaded'));
    });
  }

  function upload(event) {
    event.preventDefault();
    return perform(async () => {
      if (!file) throw new Error(t('item_limits.file_required'));
      const body = new FormData();
      body.append('file', file);
      const response = await request('/upload', { method: 'POST', body });
      const data = await response.json();
      setFile(null);
      setFileKey(previous => previous + 1);
      setFeedback(t('item_limits.uploaded', { count: data.uploaded }));
      await load();
    });
  }

  return <div className="dashboard-panel">
    <h2>{t('dashboard_tab_item_limits')}</h2>
    <div className="items-status-row" aria-live="polite">
      {feedback && <p className="items-success" role="status">{feedback}</p>}
      {error && <p className="message" role="alert">{error}</p>}
    </div>
    <div className="items-layout">
      <form className="items-editor-card" onSubmit={save}>
        <div className="items-editor-header"><h3>{t('item_limits.add')}</h3></div>
        <LimitFields prefix="new-limit" companyId={companyId} value={form} onChange={setForm} disabled={busy} />
        <p className="items-helper-text">{t('item_limits.session_help')}</p>
        <p className="items-helper-text">{t('item_limits.limit_help')}</p>
        <button className="btn" disabled={busy}>{busy ? t('item_limits.working') : t('item_limits.add')}</button>
      </form>
      <div className="items-side-panel">
        <form className="items-filter-card" onSubmit={event => { event.preventDefault(); setError(''); setFilters({ ...filterDraft }); }}>
          <div className="items-section-head"><h3>{t('filters')}</h3></div>
          <div className="items-filter-grid">
            {['receiver_company_id', 'item_barcode'].map(field => <div className="field" key={field}>
              <label htmlFor={`limit-filter-${field}`}><strong>{t(`item_limits.${field}`)}</strong></label>
              <input id={`limit-filter-${field}`} className="input" value={filterDraft[field]}
                onChange={event => setFilterDraft(previous => ({ ...previous, [field]: event.target.value }))} />
            </div>)}
          </div>
          <button className="btn" disabled={loading || busy}>{t('item_limits.apply')}</button>
        </form>
        <div className="items-tools">
          {['template', 'export'].map(kind => <div className="items-tool-card" key={kind}>
            <h3>{t(`item_limits.${kind}_title`)}</h3>
            <p>{t(`item_limits.${kind}_text`)}</p>
            <button className="btn" disabled={busy} onClick={() => download(kind)}>{t(`item_limits.${kind}_button`)}</button>
          </div>)}
          <form className="items-tool-card" onSubmit={upload}>
            <h3>{t('item_limits.upload_title')}</h3>
            <p>{t('item_limits.upload_text')}</p>
            <input key={fileKey} type="file" accept=".xlsx" required className="input items-file-input"
              aria-label={t('item_limits.upload_title')} disabled={busy}
              onChange={event => { setFile(event.target.files?.[0] || null); setError(''); setFeedback(''); }} />
            <button className="btn" disabled={busy}>{busy ? t('item_limits.working') : t('item_limits.upload_button')}</button>
          </form>
        </div>
      </div>
    </div>
    <div className="items-table-wrap" style={{ overflowX: 'auto' }}>
      <h3>{t('dashboard_tab_item_limits')}</h3>
      {loading ? <p>{t('item_limits.loading')}</p> : rows.length ? <table className="orders-table">
        <thead><tr><th>#</th>{fields.map(field => <th key={field}>{t(`item_limits.${field}`)}</th>)}<th>{t('item_limits.actions')}</th></tr></thead>
        <tbody>{rows.map((row, index) => <tr key={row.id}>
          <td className="col-num">{index + 1}</td>
          {fields.map(field => <td key={field}>{row[field]}</td>)}
          <td className="items-actions-cell">
            <button className="btn action-btn" aria-label={t('edit')} title={t('edit')} disabled={busy} onClick={() => { setError(''); setEdit({ ...row }); }}>✎</button>
            <button className="btn ghost action-btn action-btn-danger" aria-label={t('delete')} title={t('delete')} disabled={busy} onClick={() => remove(row)}>✕</button>
          </td>
        </tr>)}</tbody>
      </table> : <p>{t('item_limits.empty')}</p>}
    </div>
    {edit && <div className="modal-backdrop" onClick={() => { if (!busy) setEdit(null); }}>
      <div className="modal-card items-edit-modal" role="dialog" aria-modal="true" aria-labelledby="edit-limit-title" onClick={event => event.stopPropagation()}>
        <div className="modal-header">
          <strong id="edit-limit-title">{t('item_limits.edit')}</strong>
          <button className="modal-close" aria-label={t('close')} disabled={busy} onClick={() => setEdit(null)}>×</button>
        </div>
        <form className="modal-body" onSubmit={event => save(event, true)}>
          {error && <p className="message" role="alert">{error}</p>}
          <LimitFields prefix="edit-limit" companyId={companyId} value={edit} onChange={setEdit} disabled={busy} />
          <div className="items-modal-actions">
            <button className="btn" disabled={busy}>{busy ? t('item_limits.working') : t('item_limits.save')}</button>
            <button type="button" className="btn ghost" disabled={busy} onClick={() => setEdit(null)}>{t('close')}</button>
          </div>
        </form>
      </div>
    </div>}
  </div>;
}
